import { NetworkStatus } from '@apollo/client';
import { makeStyles, Slide, Theme } from '@material-ui/core';
import React, { useCallback, useMemo, useRef } from 'react';

import { PostType } from '../../../graphql/gen/types';
import { useGetPostsQuery } from '../../../graphql/queries/GetPosts';
import { usePublishStatusFilter } from '../../../models/filters/usePublishStatusFilter';
import { useSelectedLabelIds } from '../../../models/filters/useSelectedLabelIds';
import { useSelectedPlexIds } from '../../../models/filters/useSelectedPlexIds';
import { colorPalette, spacings } from '../../../theme';
import { getPublishStatuses } from '../../../utils/permissions';
import { FilterPicker } from '../common/FilterPicker';
import { useSelectedPlexPost } from '../details/post/useSelectedPlexPost';
import { NewPostButton } from '../post_form/NewPostButton';
import { AnalyticsLocation, useLogAnalytics } from '../shared/logAnalytics';
import { EmptyPostTimeline } from './EmptyPostTimeline';
import { FooterLoader } from './FooterLoader';
import { PlexPost } from './PlexPost';
import { FetchMoreCallback, useInfiniteScroll } from './useInfiniteScroll';
import { useItemInViewport } from './useItemInViewport';
import { useSubscribeToMorePosts } from './useSubscribeToMorePosts';

export const PlexPostTimeline = React.memo(
  (): React.ReactElement => {
    useLogAnalytics({ name: 'loaded page', details: { location: AnalyticsLocation.FEED } });
    const classes = useStyles();
    const [selectedPlexPost, setSelectedPlexPost] = useSelectedPlexPost();
    const plexIds = useSelectedPlexIds();
    const filterPublishStatus = usePublishStatusFilter();
    const labelIds = useSelectedLabelIds();
    const publishStatuses = useMemo(() => getPublishStatuses(filterPublishStatus), [filterPublishStatus]);
    const variables = useMemo(
      () => ({
        plexIds,
        publishStatuses,
        labelIds,
        types: [PostType.Post],
      }),
      [labelIds, plexIds, publishStatuses]
    );
    const { data, loading: initialLoading, subscribeToMore, fetchMore, networkStatus } = useGetPostsQuery({
      variables,
    });
    const posts = data?.posts.items || [];
    const nextCursor = data?.posts.nextCursor;
    const selectedPlexPostPermalink = selectedPlexPost?.permalink;
    const timelineContainerRef = useRef<HTMLDivElement>(null);
    const onFetchMore: FetchMoreCallback = useCallback(
      (isCancelled) =>
        fetchMore({
          variables: { ...variables, cursor: nextCursor },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult || !fetchMoreResult.posts || isCancelled()) return prev;
            return {
              ...prev,
              posts: {
                ...prev.posts,
                items: [...(prev.posts.items || []), ...fetchMoreResult.posts.items],
                nextCursor: fetchMoreResult.posts.nextCursor,
              },
            };
          },
        }),
      [fetchMore, nextCursor, variables]
    );
    const infiniteScrollLoading = useInfiniteScroll({
      containerRef: timelineContainerRef,
      onFetchMore,
      canFetchMore: !initialLoading && nextCursor != null,
    });
    const loading = initialLoading || infiniteScrollLoading;

    useSubscribeToMorePosts(subscribeToMore, variables);

    const handleSelectPlexPost = useCallback(
      (id: string): void => {
        setSelectedPlexPost({ permalink: id, commentsVisible: false });
      },
      [setSelectedPlexPost]
    );

    const filteredPosts = networkStatus === NetworkStatus.setVariables ? [] : posts.filter((post) => !post.deleted);
    const visibleIds = useItemInViewport(timelineContainerRef, 'plexPost_', 0.8);

    return (
      <Slide direction={'up'} in={true} mountOnEnter={true} unmountOnExit={true}>
        <div className={classes.root} ref={timelineContainerRef}>
          <div className={classes.header}>
            <FilterPicker />
            <NewPostButton type={'post'} className={classes.createBtn} />
          </div>

          {!loading && filteredPosts.length === 0 && <EmptyPostTimeline />}

          {filteredPosts.map((plexPost) => (
            <PlexPost
              id={`plexPost_${plexPost.id}`}
              key={plexPost.id}
              plexPost={plexPost}
              onSelect={handleSelectPlexPost}
              selected={selectedPlexPostPermalink != null && selectedPlexPostPermalink === plexPost.permalink}
              visible={visibleIds.has(plexPost.id)}
            />
          ))}
          <FooterLoader isLoading={loading} />
        </div>
      </Slide>
    );
  }
);

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: `0 ${spacings.normal}px`,
    overflowY: 'auto',
    maxWidth: '960px',
  },
  createBtn: {
    flexShrink: 0,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    padding: spacings.normal,
    margin: `0 -${spacings.normal}px`,
    justifyContent: 'space-between',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    left: 0,
    backgroundColor: colorPalette.general.feedBackground,
    zIndex: 10,
    alignItems: 'center',
  },
}));
